import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { 
  getPageSEO, 
  generateOGTags, 
  generateTwitterTags, 
  DEFAULT_IMAGE 
} from '../../utils/seo';

const SEOHead = ({ 
  pageName, 
  customTitle, 
  customDescription, 
  customKeywords,
  customImage,
  customStructuredData,
  noIndex = false 
}) => {
  const location = useLocation();
  
  // Get SEO data based on page name or current location
  const pageKey = pageName || location.pathname.replace('/', '') || 'home';
  const seoData = getPageSEO(pageKey);
  
  // Override with custom data if provided
  const title = customTitle || seoData.title;
  const description = customDescription || seoData.description;
  const keywords = customKeywords || seoData.keywords;
  const canonical = seoData.canonical;
  const image = customImage || DEFAULT_IMAGE;
  
  // Generate meta tags
  const ogTags = generateOGTags({ 
    ...seoData, 
    title, 
    description, 
    canonical,
    image 
  });
  const twitterTags = generateTwitterTags({ 
    ...seoData, 
    title, 
    description, 
    image 
  });
  
  // Structured data
  const structuredData = customStructuredData || seoData.structured;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />
      
      {/* Robots */}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Open Graph */}
      <meta property="og:title" content={ogTags['og:title']} />
      <meta property="og:description" content={ogTags['og:description']} />
      <meta property="og:url" content={ogTags['og:url']} />
      <meta property="og:type" content={ogTags['og:type']} />
      <meta property="og:image" content={ogTags['og:image']} />
      <meta property="og:site_name" content={ogTags['og:site_name']} />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterTags['twitter:card']} />
      <meta name="twitter:title" content={twitterTags['twitter:title']} />
      <meta name="twitter:description" content={twitterTags['twitter:description']} />
      <meta name="twitter:image" content={twitterTags['twitter:image']} />
      <meta name="twitter:site" content={twitterTags['twitter:site']} />
      <meta name="twitter:creator" content={twitterTags['twitter:creator']} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="Mailsball" />
      <meta name="publisher" content="Mailsball" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#22c55e" />
      
      {/* Structured Data */}
      {structuredData && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ 
            __html: JSON.stringify(structuredData) 
          }}
        />
      )}
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      
      {/* DNS prefetch for common domains */}
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      
      {/* Preload critical resources */}
      <link rel="preload" href="/images/logo.svg" as="image" type="image/svg+xml" />
    </Helmet>
  );
};

export default SEOHead; 